import classNames from 'clsx';
import React, { useRef, useState } from 'react';
import { Popper } from 'react-popper';
import { useSelector } from 'react-redux';

import { FeatureFlag } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import getBackgroundImageStyle from '@swf/core/utils/getBackgroundImageStyle';
import { CasinoIcon } from '@swf/generated/types/common/Casino';

import BonusFeatures from '../BonusFeatures/BonusFeatures';
import CloseButton from '../Buttons/CloseButton';
import InfoPlayButton from '../Buttons/InfoPlayButton';
import WatchTrailerButton from '../Buttons/WatchTrailerButton';
import CasinoGameIcon from '../CasinoGameIcon/CasinoGameIcon';
import GameTrailerIcon from '../GameTrailerIcon/GameTrailerIcon';

import styles from './CasinoGameInfoCardMax.scss';
import BetInfo from './components/BetInfo';

interface Props {
  isModal?: boolean;
  fsbLaunch?: boolean;
  raceId?: string;
  game?: CasinoGame;
}

const CasinoGameInfoCardMax: React.FC<Props> = (props) => {
  const { fsbLaunch, game, isModal, raceId } = props;
  const [detailVisible, setDetailVisible] = useState<boolean>(false);
  const [videoVisible, setVideoVisible] = useState<boolean>(false);
  const ref = useRef(null);
  const [betFeatureCount, setBetFeatureCount] = useState<number>(-1);
  const descriptionEnabled = useFeature(FeatureFlag.CasinoGameInfoDescription);

  const siteId = useSelector(select.cms.getSiteId);
  const isMobile = useSelector(select.device.isMobile);
  const table = game?.tables[0];
  const features = table?.featureNames;
  const trailerAvailable = game?.images[CasinoIcon.icnGameTrailerVideo];

  return game ? (
    <div
      className={classNames(styles.container, isModal && styles.modal)}
      onClick={(evt) => evt.stopPropagation()}
    >
      <div className={classNames(styles.gamePage)}>
        <div className={styles.title}>{game.name}</div>
        {isModal && (
          <CloseButton
            className={styles.closeButton}
            closeModal
            onClick={() => setVideoVisible(false)}
          />
        )}
        {(detailVisible || videoVisible) && (
          <div className={classNames(styles.popper, styles.popperOverlay)} onClick={() => false} />
        )}
        {(detailVisible || videoVisible) && (
          <Popper>
            {() =>
              detailVisible ? (
                <BonusFeatures
                  className={classNames(styles.popper, styles.detailPopper)}
                  featureNames={table?.featureNames || []}
                  onClose={() => setDetailVisible(false)}
                />
              ) : (
                <div className={classNames(styles.popper, styles.videoPopper)}>
                  <CloseButton
                    className={styles.closeButton}
                    onClick={() => {
                      setVideoVisible(false);
                    }}
                  />
                  <GameTrailerIcon game={game} autoPlay />
                </div>
              )
            }
          </Popper>
        )}
        <div className={classNames(styles.imageContainer)}>
          {
            <CasinoGameIcon
              game={game}
              disabled
              scale={false}
              className={styles.gameIcon}
              icon={isMobile ? CasinoIcon.icnWide : CasinoIcon.icnSquare}
            />
          }
        </div>
        <div className={classNames(styles.launchBlock, trailerAvailable && styles.withTrailer)}>
          <InfoPlayButton game={game} fsbLaunch={fsbLaunch} raceId={raceId} />

          {trailerAvailable && (
            <WatchTrailerButton
              onClick={() => {
                setVideoVisible(true);
              }}
            />
          )}
        </div>
        <div
          className={classNames(
            styles.detailsContainer,
            features && styles.features,
            betFeatureCount === 0 && styles.noBetInfo
          )}
          ref={ref}
        >
          <BonusFeatures
            featureNames={table?.featureNames || []}
            onClick={() => setDetailVisible(true)}
          />

          {betFeatureCount !== 0 && table && (
            <BetInfo
              setBetFeatureCount={setBetFeatureCount}
              data={table}
              siteId={siteId}
              shortText={true}
            />
          )}
          {betFeatureCount < 0 && <div />}

          {descriptionEnabled && game.desc && (
            <div className={classNames(styles.description)}>
              <p
                className={styles.gameDescription}
                dangerouslySetInnerHTML={{ __html: game.desc }}
              />
            </div>
          )}
        </div>
      </div>
      {game.images && (
        <div
          className={styles.infoBackground}
          style={getBackgroundImageStyle(game.images.icnSquare)}
        />
      )}
    </div>
  ) : null;
};

export default CasinoGameInfoCardMax;
